import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { Wrench, CircleNotch, Plus, Trash, CheckCircle } from "@phosphor-icons/react";

const DUE = ["distance", "hours", "date"];
const POS = ["FL", "FR", "RL", "RR", "Spare"];
const TSTATUS = ["stock", "fitted", "retired"];

export default function Maintenance() {
  const [tab, setTab] = useState("reminders");
  const [reminders, setReminders] = useState([]);
  const [tyres, setTyres] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [r, setR] = useState({ vehicle_number: "", service_type: "", due_type: "distance", due_value: "", note: "" });
  const [t, setT] = useState({ serial: "", brand: "", size: "", position: "FL", vehicle_number: "", tread_mm: "", status: "stock" });

  const loadR = () => api.get("/service-reminders").then((x) => setReminders(x.data.reminders)).catch(() => {});
  const loadT = () => api.get("/tyres").then((x) => setTyres(x.data.tyres)).catch(() => {});
  useEffect(() => { loadR(); loadT(); api.get("/vehicles").then((x) => setVehicles(x.data.vehicles)).catch(() => {}); }, []);

  const addReminder = async () => {
    if (!r.service_type.trim()) return toast.error("Service type required");
    try { await api.post("/service-reminders", r); toast.success("Reminder added"); setR({ vehicle_number: "", service_type: "", due_type: "distance", due_value: "", note: "" }); loadR(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markDone = async (id) => { await api.patch(`/service-reminders/${id}`); toast.success("Marked done"); loadR(); };
  const delReminder = async (id) => { await api.delete(`/service-reminders/${id}`); loadR(); };

  const addTyre = async () => {
    if (!t.serial.trim()) return toast.error("Tyre serial required");
    try { await api.post("/tyres", { ...t, tread_mm: parseFloat(t.tread_mm) || 0 }); toast.success("Tyre added"); setT({ serial: "", brand: "", size: "", position: "FL", vehicle_number: "", tread_mm: "", status: "stock" }); loadT(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delTyre = async (id) => { await api.delete(`/tyres/${id}`); loadT(); };

  const remCols = [
    { key: "vehicle_number", header: "Vehicle", render: (x) => x.vehicle_number || "—" },
    { key: "service_type", header: "Service", render: (x) => <span className="font-semibold">{x.service_type}</span> },
    { key: "due", header: "Due", render: (x) => `${x.due_value || "—"} ${x.due_type === "distance" ? "km" : x.due_type === "hours" ? "hrs" : ""}` },
    { key: "note", header: "Note" },
    { key: "status", header: "Status", render: (x) => x.status === "done" ? <Pill color="#34C759">done</Pill> : <Pill color="#FF9500">open</Pill> },
    { key: "act", header: "", render: (x) => (
      <span className="flex gap-2">
        {x.status !== "done" && <button data-testid={`rem-done-${x.id}`} onClick={() => markDone(x.id)} className="text-muted-foreground hover:text-[#34C759]"><CheckCircle size={16} weight="bold" /></button>}
        <button data-testid={`rem-del-${x.id}`} onClick={() => delReminder(x.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} weight="bold" /></button>
      </span>
    ) },
  ];
  const tyreCols = [
    { key: "serial", header: "Serial", render: (x) => <span className="font-mono text-xs">{x.serial}</span> },
    { key: "brand", header: "Brand" },
    { key: "size", header: "Size" },
    { key: "position", header: "Position" },
    { key: "vehicle_number", header: "Vehicle", render: (x) => x.vehicle_number || "—" },
    { key: "tread_mm", header: "Tread", render: (x) => `${x.tread_mm} mm` },
    { key: "status", header: "Status", render: (x) => <Pill color={x.status === "fitted" ? "#34C759" : x.status === "retired" ? "#8E8E93" : "#5AC8FA"}>{x.status}</Pill> },
    { key: "act", header: "", render: (x) => <button data-testid={`tyre-del-${x.id}`} onClick={() => delTyre(x.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} weight="bold" /></button> },
  ];

  const inp = "bg-background border border-border rounded-sm px-2 py-2 text-sm";

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Maintenance" subtitle="Service reminders & tyre inventory" />
      <div className="flex gap-1 border-b border-border">
        {[["reminders", "Service Reminders", Wrench], ["tyres", "Tyre Management", CircleNotch]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`maint-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 -mb-px border-b-2 ${tab === id ? "border-primary text-white" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {tab === "reminders" ? (
        <>
          <div className="bg-card border border-border rounded-sm p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
            <select value={r.vehicle_number} onChange={(e) => setR({ ...r, vehicle_number: e.target.value })} className={inp}><option value="">All / none</option>{vehicles.map((v) => <option key={v.id}>{v.vehicle_number}</option>)}</select>
            <input data-testid="rem-type" value={r.service_type} onChange={(e) => setR({ ...r, service_type: e.target.value })} placeholder="Service type *" className={inp} />
            <select value={r.due_type} onChange={(e) => setR({ ...r, due_type: e.target.value })} className={inp}>{DUE.map((d) => <option key={d}>{d}</option>)}</select>
            <input data-testid="rem-value" value={r.due_value} onChange={(e) => setR({ ...r, due_value: e.target.value })} placeholder="Due value" className={inp} />
            <input value={r.note} onChange={(e) => setR({ ...r, note: e.target.value })} placeholder="Note" className={inp} />
            <button data-testid="rem-add" onClick={addReminder} className="bg-primary text-white font-semibold text-sm py-2 rounded-sm flex items-center justify-center gap-1"><Plus size={15} weight="bold" /> Add</button>
          </div>
          <DataTable testid="reminders-table" columns={remCols} rows={reminders} empty="No service reminders yet" />
        </>
      ) : (
        <>
          <div className="bg-card border border-border rounded-sm p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
            <input data-testid="tyre-serial" value={t.serial} onChange={(e) => setT({ ...t, serial: e.target.value })} placeholder="Serial *" className={inp} />
            <input value={t.brand} onChange={(e) => setT({ ...t, brand: e.target.value })} placeholder="Brand" className={inp} />
            <input value={t.size} onChange={(e) => setT({ ...t, size: e.target.value })} placeholder="Size" className={inp} />
            <input type="number" min="0" step="0.5" value={t.tread_mm} onChange={(e) => setT({ ...t, tread_mm: e.target.value })} placeholder="Tread mm" className={inp} />
            <select value={t.position} onChange={(e) => setT({ ...t, position: e.target.value })} className={inp}>{POS.map((p) => <option key={p}>{p}</option>)}</select>
            <select value={t.vehicle_number} onChange={(e) => setT({ ...t, vehicle_number: e.target.value })} className={inp}><option value="">Stock</option>{vehicles.map((v) => <option key={v.id}>{v.vehicle_number}</option>)}</select>
            <select value={t.status} onChange={(e) => setT({ ...t, status: e.target.value })} className={inp}>{TSTATUS.map((s) => <option key={s}>{s}</option>)}</select>
            <button data-testid="tyre-add" onClick={addTyre} className="bg-primary text-white font-semibold text-sm py-2 rounded-sm flex items-center justify-center gap-1"><Plus size={15} weight="bold" /> Add</button>
          </div>
          <DataTable testid="tyres-table" columns={tyreCols} rows={tyres} empty="No tyres in inventory yet" />
        </>
      )}
    </div>
  );
}
